import {
    Admin,
    Author,
    PublishingAuthor,
    Reviewer,
} from '@deltek/specification-client/core/Configurations/Authorization'
import ProductListingLevels from '@deltek/specification-client/core/Constants/productListingLevels'
import { AccountType } from '@deltek/specification-client/core/Models/accountType'
import {
    CurrentUserInfo,
    CurrentUserInfoResponse,
} from '@deltek/specification-client/core/Models/currentUserInfo'
import { ListingLevel } from '@deltek/specification-client/core/Models/product'
import { ProductTypePropertiesInputType } from '@deltek/specification-client/core/Models/productType'
import { SearchFacetInput } from '@deltek/specification-client/core/Models/searchFacetInput'
import {
    PartnershipsOption,
    StatusOption,
} from '@deltek/specification-client/core/Widgets/ProductListing/Filter/MoreFilterSubCategories'
import { NormalizedProductType } from '@deltek/specification-client/core/Widgets/ProductListing/model'
import {
    escapeElementDelimiter,
    unescapeElementDelimiter,
} from '@deltek/specification-client/core/Widgets/ProductListing/productsUtils'
import {
    queryNameToDateParamMap,
    queryNameToFacetNameMap,
} from 'bpm/pages/ProductsPage/queryNameMaps'
import { History, Location } from 'history'
import sanitizeHtml from 'sanitize-html'
import { removeFamilyNumberFromFamilyNameRegex } from './components/common'
import { ListingStatus } from './ProductDetailsPage/components/ListingHeaderVariants/ListingStatusVariant'
export interface IsUserBPMFirmAdminOrDeltekAdminProps {
    currentUserInfo?: CurrentUserInfo
    firmId?: string
}

export enum UserType {
    Deltek = 'System',
    BPM = 'BuildingProductManufacturer',
    AEfirm = 'AEFirm',
}

const queryParamDifferentiator = ','
/**
 * Returns true when a user is either a BPM Admin of the firmId or a Deltek Admin
 */

export const isUserBPMFirmAdminOrDeltekAdmin = ({
    currentUserInfo,
    firmId,
}: IsUserBPMFirmAdminOrDeltekAdminProps) => {
    if (!currentUserInfo || !firmId) return false

    const accountType = currentUserInfo.account.accountType as AccountType

    const isAdmin = currentUserInfo.roles.find((role) => role === Admin)
    if (!isAdmin) return false

    const isBpmUser = accountType === UserType.BPM
    const isDeltekUser = accountType === UserType.Deltek
    if (!isBpmUser && !isDeltekUser) return false

    // check if user is a member of the firm
    if (isBpmUser) {
        return firmId === currentUserInfo.account.accountId
    }

    return true
}

export interface IsUserDeltekAdminProps {
    currentUserInfo?: CurrentUserInfo
}

/**
 * Returns true when a user is Deltek Admin
 */
export const isUserDeltekAdmin = ({
    currentUserInfo,
}: IsUserDeltekAdminProps) => {
    if (!currentUserInfo) return false

    const accountType = currentUserInfo.account.accountType as AccountType

    const isAdmin = currentUserInfo.roles.find((role) => role === Admin)
    if (!isAdmin) return false

    const isDeltekUser = accountType === UserType.Deltek
    if (!isDeltekUser) return false

    return true
}

/**
 * Returns true when a user is BPM Admin
 */
export const isUserBPMAdmin = (
    userinfo: CurrentUserInfoResponse | undefined
) => {
    const isBPMUser =
        userinfo?.currentUserInfo?.account.accountType === UserType.BPM
    const isAdmin = userinfo?.currentUserInfo?.roles.includes(Admin)
    const isAuthor = userinfo?.currentUserInfo?.roles.includes(Author)
    const isReviewer = userinfo?.currentUserInfo?.roles.includes(Reviewer)
    return {
        isBPM: isBPMUser,
        isBPMAdmin: !!(isBPMUser && isAdmin),
        isBPMAuthor: !!(isBPMUser && isAuthor),
        isBPMReviewer: !!(isBPMUser && isReviewer),
    }
}

export const isDeltekUserorAdmin = (
    userinfo: CurrentUserInfoResponse | undefined
) => {
    const isDeltekUser =
        userinfo?.currentUserInfo?.account.accountType === UserType.Deltek
    const isAdmin = userinfo?.currentUserInfo?.roles.includes(Admin)
    const isPublishingAuthor = userinfo?.currentUserInfo?.roles.includes(
        PublishingAuthor
    )
    const isAuthor = userinfo?.currentUserInfo?.roles.includes(Author)
    const isReviewer = userinfo?.currentUserInfo?.roles.includes(Reviewer)
    return {
        isDeltekUser: isDeltekUser,
        isDeltekAuthor: !!(isDeltekUser && isAuthor),
        isDeltekAdmin: !!(isDeltekUser && isAdmin),
        isDeltekPublishingAuthor: !!(isDeltekUser && isPublishingAuthor),
        isDeltekReviewer: !!(isDeltekUser && isReviewer),
    }
}

//RTF utils
export const overriddenFontStyle = {
    fontFamily: 'inherit !important',
    fontSize: 'inherit !important',
    lineHeight: 'inherit !important',
    textDecoration: 'none !important',
    color: 'inherit !important',
    border: 'none !important',
    width: 'auto !important',
}
export const sanitizeConf = {
    allowedTags: [
        'b',
        'i',
        'em',
        'strong',
        'div',
        'ul',
        'li',
        'ol',
        'p',
        'span',
        'blockquote',
    ],
}
export const clipBoardHandler = (
    event: React.ClipboardEvent<HTMLDivElement>,
    limit?: number
) => {
    event.preventDefault()
    let htmlText = event.clipboardData.getData('text/html')

    if (htmlText?.includes('mso'))
        htmlText = htmlText?.replace(/(\r\n|\n|\r)/gm, '')
    htmlText = sanitizeHtml(htmlText, sanitizeConf)?.replace(/^\s+|\s+$/g, '')

    const plainText = event.clipboardData.getData('text/plain')

    let textFieldData = htmlText ? htmlText : plainText
    if (htmlText?.trim() === plainText?.trim()) textFieldData = plainText
    document.execCommand('insertHTML', false, textFieldData.slice(0, limit))
}

export const updateQueryString = (
    filterSelection: Record<string, string[]> | undefined,
    inputSelection: Record<string, Date | string | null> | undefined,
    history: History<unknown>,
    location: Location<unknown>,
    saveURLSearchParams?: (newQS: string) => void
): void => {
    let newQS: string = ''
    let queryParams = new URLSearchParams(location.search)
    if (filterSelection) {
        queryNameToFacetNameMap.forEach((each) => {
            const vals = filterSelection[each.facetName]?.map((v) =>
                escapeElementDelimiter(v)
            )

            queryParams.set(each.queryName, vals?.join(','))
        })
    }

    if (inputSelection) {
        for (let index in queryNameToDateParamMap) {
            let val = inputSelection[
                queryNameToDateParamMap[index].facetName
            ]?.toString()
            queryParams.set(queryNameToDateParamMap[index].queryName, val ?? '')
        }
    }

    // Filter undefined values from querystring
    queryParams.forEach(function (value, key) {
        if (value && value !== 'undefined') {
            newQS += `${key}=${encodeURIComponent(value)}&`
        }
    })

    // Cleanup
    if (newQS.endsWith('&')) {
        newQS = newQS.slice(0, -1)
    }

    saveURLSearchParams?.(`?${newQS}`)
    history.push(`${location.pathname}${!newQS.length ? '' : '?'}${newQS}`)
}

export const getSectionName = (value?: string) =>
    value ? value.split('%|%')?.[0]?.slice(1, -1) : ''

//value = section name and product type
export const getProductTypeName = (value?: string) =>
    value ? value.split('%|%')?.[1]?.slice(1, -1) : ''

export const getFacetInputsFromFilterSelection = (
    filterSelectionData: Record<string, string[]> | undefined
): SearchFacetInput[] => {
    let facets: SearchFacetInput[] = []
    const filterSelection: Record<string, string[]> | undefined = {
        ...filterSelectionData,
    }
    for (let facetName in filterSelection) {
        const facetValues: string[] = filterSelection[facetName]
        if (
            facetValues &&
            facetValues.length &&
            facetName !== 'additionalContent' &&
            facetName !== 'orderBy'
        ) {
            facets = [
                ...facets,
                {
                    name: facetName,
                    values: [...facetValues],
                },
            ]
        }
    }
    return facets
}

export const getInputSelectionValuesFromQueryString = (
    querystring: string
): Record<string, Date | string | null> => {
    let inputSelectionValues: Record<string, Date | string | null> = {}
    let queryParams = new URLSearchParams(querystring)

    for (let index in queryNameToDateParamMap) {
        let val = queryParams.get(queryNameToDateParamMap[index].queryName)
        if (val) {
            inputSelectionValues[
                queryNameToDateParamMap[index].facetName
            ] = decodeURIComponent(unescapeElementDelimiter(val))
        }
    }
    return inputSelectionValues
}

export const getSelectedProductTypesFromQueryString = (
    querystring: string
): NormalizedProductType[] => {
    let selectedPTs: NormalizedProductType[] = []
    let queryParams = new URLSearchParams(querystring)
    let vals = queryParams.get('sp')?.split(',') //sectionNameWithProductType
    let ptpString = decodeURIComponent(
        (queryParams.has('ptp') && queryParams.get('ptp')) || ''
    ) //productTypeproperties

    let productTypeproperties: Record<
        string,
        ProductTypePropertiesInputType[]
    > = {}
    if (ptpString) {
        ptpString.split(';')?.forEach((ptp) => {
            const ptpStringArr = ptp?.split('%|%')

            const sectionName = ptpStringArr?.[0]
            const typeName = ptpStringArr?.[1]
            const catWithValues = ptpStringArr?.[2]

            const [category, values] = catWithValues?.split(':') ?? []
            productTypeproperties[
                `'${sectionName}'%|%'${typeName}'`
            ] = !values?.length
                ? []
                : productTypeproperties[`${sectionName}'%|%'${typeName}`]
                ? [
                      ...productTypeproperties[typeName],
                      {
                          propertyName: category,
                          propertyOptionIds: values?.split(','),
                      },
                  ]
                : [
                      {
                          propertyName: category,
                          propertyOptionIds: values?.split(','),
                      },
                  ]
        })
    }

    if (vals) {
        selectedPTs = vals.map((v) => {
            const unescapedVal = unescapeElementDelimiter(v)

            return {
                name: unescapedVal,
                value: unescapedVal,
                sectionNameWithoutNmber: getSectionName(unescapedVal)?.replace(
                    removeFamilyNumberFromFamilyNameRegex,
                    ''
                ),
                sectionName: getSectionName(unescapedVal),
                productTypeName: getProductTypeName(unescapedVal),
                productTypeProperties: (productTypeproperties[
                    getProductTypeName(unescapedVal)
                ] ?? []) as ProductTypePropertiesInputType[],
            }
        })
    }

    const returnVal = selectedPTs.length
        ? [
              ...Array.from(
                  new Set([
                      ...selectedPTs.filter(
                          (selectedPT) =>
                              !Object.keys(productTypeproperties).includes(
                                  selectedPT.name
                              )
                      ),
                      ...Object.entries(productTypeproperties).map(
                          ([key, value]) => {
                              const unescapedVal = key

                              return {
                                  name: unescapedVal,
                                  value: unescapedVal,
                                  sectionNameWithoutNmber: getSectionName(
                                      unescapedVal
                                  )?.replace(
                                      removeFamilyNumberFromFamilyNameRegex,
                                      ''
                                  ),
                                  sectionName: getSectionName(unescapedVal),
                                  productTypeName: getProductTypeName(
                                      unescapedVal
                                  ),
                                  productTypeProperties: value,
                              }
                          }
                      ),
                  ])
              ),
          ]
        : []

    return returnVal
}

export const getSelectedValuesFromQueryString = (
    querystring: string
): Record<string, string[]> => {
    let selectedValues: Record<string, string[]> = {}

    // Setting isActive default to true for now for EA.
    querystring = querystring.length > 0 ? querystring : 'ia=true'

    let queryParams = new URLSearchParams(querystring)

    for (let index in queryNameToFacetNameMap) {
        let vals = queryParams
            .get(queryNameToFacetNameMap[index].queryName)
            ?.split(queryParamDifferentiator)

        if (vals && vals.length > 0) {
            selectedValues[
                queryNameToFacetNameMap[index].facetName
            ] = vals.map((v) => unescapeElementDelimiter(v))
        }
    }

    const ptValues = getSelectedProductTypesFromQueryString(querystring)

    if (ptValues.length) {
        selectedValues['sectionNameWithProductType'] = ptValues.map(
            (pt) => pt.value
        )
    }

    return selectedValues
}
export const isAdditionalContentSelected = (
    additionalContentFilter: string[],
    catValue: string
): boolean | undefined => {
    let isSelected: boolean = false
    if (additionalContentFilter && additionalContentFilter.length) {
        isSelected = additionalContentFilter.some(
            (category: string) => category === catValue
        )
    }
    if (isSelected) return true
    return undefined
}

export const createFilter = (
    facets: SearchFacetInput[],
    searchType: 'Search' | 'AutoSuggest',
    facetName?: string
): SearchFacetInput[] => {
    const facetNames = facets.map((each) => each.name)

    const withManufacturerOption = facetNames.some(
        (each) => each === 'withManufacturerOption'
    )
    const manufacturerOptionsDefaultLevels: string[] = [
        ListingLevel.Premium,
        ListingLevel.PremiumMasterSpec,
    ]
    const manufacturerOptionsExcludedLevels: string[] = [
        ListingLevel.PremiumProprietary,
        ListingLevel.Manufacturer,
    ]

    let filteredFacets = facets.filter(
        (each) => each.name !== 'sectionNameWithProductType'
    )

    if (
        searchType === 'AutoSuggest' &&
        facetName === 'sectionNameWithProductType'
    ) {
        {
            const productTypeSectionNames = facets
                .filter(
                    (each) => each.name === 'sectionNameWithProductType'
                )?.[0]
                ?.values.map((each) => getSectionName(each)?.split(',')?.[0])

            if (productTypeSectionNames?.length > 0) {
                const hasSectionNames = filteredFacets.some(
                    (each) => each.name === 'sectionNames'
                )

                if (hasSectionNames) {
                    return filteredFacets.map((each) => {
                        if (each.name === 'sectionNames') {
                            return {
                                ...each,
                                values: Array.from(
                                    new Set([
                                        ...each.values,
                                        ...(productTypeSectionNames ?? []),
                                    ])
                                ),
                            }
                        }
                        return each
                    })
                }

                return [
                    {
                        name: 'sectionNames',
                        values: productTypeSectionNames,
                    },
                    ...filteredFacets,
                ]
            }
        }
    }

    if (
        facetName === 'sectionNames' ||
        facetName === 'sectionNameWithProductType'
    ) {
        return filteredFacets
            .map((facet) => {
                if (facet.name === 'listingLevels' && withManufacturerOption) {
                    const listingLevelValues = facet.values.filter(
                        (value) =>
                            !manufacturerOptionsExcludedLevels.includes(value)
                    )
                    return { ...facet, values: listingLevelValues }
                }
                return facet
            })
            .filter((each) => each.name !== 'withManufacturerOption')
    } else {
        filteredFacets = filteredFacets.map((facet) => {
            if (facet.name === 'listingLevels' && withManufacturerOption) {
                const validListingLevels =
                    facet.values.filter(
                        (value) =>
                            !manufacturerOptionsExcludedLevels.includes(value)
                    ) || []

                return {
                    ...facet,
                    values:
                        validListingLevels.length === 0
                            ? manufacturerOptionsDefaultLevels
                            : validListingLevels,
                }
            }

            return facet
        })
    }

    return filteredFacets
}

export const getFilterDisabledValues = (
    filterFacets: Record<string, string[]>,
    hasManufacturerOptions: boolean
): Record<string, string[]> => {
    const resultListingLevels = filterFacets.ListingLevels ?? []
    const resultPartnership = filterFacets.Partnerships ?? []
    const resultSubmissionStatus = filterFacets.SubmissionStatus ?? []
    const resultStatus = filterFacets.IsActive ?? []
    const addlCon: Record<string, boolean> = {
        hasBimLinks: filterFacets?.HasBimLinks?.map((each) =>
            each?.toLowerCase()
        ).includes('true'),
        hasCatalogs: filterFacets?.HasCatalogs?.map((each) =>
            each?.toLowerCase()
        ).includes('true'),
        hasDocumentFL: filterFacets?.HasDocumentFL?.map((each) =>
            each?.toLowerCase()
        ).includes('true'),
        hasDocumentSF: filterFacets?.HasDocumentSF?.map((each) =>
            each?.toLowerCase()
        ).includes('true'),
    }

    const disabledListingLevels = ProductListingLevels.filter(
        (level) => resultListingLevels && !resultListingLevels.includes(level)
    )

    return {
        listingLevels: hasManufacturerOptions
            ? [
                  ...disabledListingLevels,
                  ListingLevel.Manufacturer,
                  ListingLevel.PremiumProprietary,
              ]
            : disabledListingLevels,
        partnerships: Object.values(PartnershipsOption).filter(
            (partnership) =>
                resultPartnership && !resultPartnership.includes(partnership)
        ),
        submissionStatus: Object.values(ListingStatus).filter(
            (state) =>
                resultSubmissionStatus &&
                !resultSubmissionStatus.includes(state)
        ),
        status: Object.keys(StatusOption).filter(
            (statusOption) =>
                resultStatus &&
                !resultStatus.includes(
                    statusOption.substr(0, 1).toUpperCase() +
                        statusOption.substr(1)
                )
        ),
        additionalContent: Object.keys(addlCon).filter(
            (addConKey) => !addlCon[addConKey]
        ),
    }
}

//Removes first element (family) and the quotes around the remaning elements
export const trimFamilyAndQuotesFromAutosuggestResponse = (
    autosuggestTerms: string[]
): string[] => {
    return (
        autosuggestTerms?.map(
            (ptn) =>
                ptn
                    ?.split('%|%')
                    ?.map((elem) => elem?.slice(1, -1))
                    ?.slice(1)
                    ?.join('%|%') ?? ''
        ) ?? []
    ).filter((rString) => !!rString)
}
